import React, { useState } from 'react';
import { Gift, Search, Loader2, Check } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const GiftProDialog = ({ open, onOpenChange }) => {
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [targetUser, setTargetUser] = useState(null);

  const findUser = async () => {
    if (!username.trim()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, username, is_pro')
        .eq('username', username.replace('@', '').trim())
        .single();
      
      if (error || !data) throw new Error("Architect not found.");
      setTargetUser(data);
    } catch (err) {
      toast.error(err.message);
      setTargetUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGift = async () => {
    if (!targetUser) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_pro: true, balance: 30.00 }) // Gift includes starting credits
        .eq('id', targetUser.id);
      
      if (error) throw error;
      toast.success(`Success! Pro gifted to @${targetUser.username}`);
      onOpenChange(false);
    } catch (err) {
      toast.error("Transfer failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-surface border-sonas-border max-w-sm rounded-[32px] p-8 text-center">
        <div className="h-12 w-12 rounded-full bg-sonas-accent/10 flex items-center justify-center mx-auto mb-4">
          <Gift className="h-6 w-6 text-sonas-accent" />
        </div>
        <DialogTitle className="font-syne text-xl">Gift Architect Pro</DialogTitle>
        <DialogDescription className="text-xs text-sonas-muted mt-2">
          Promote another architect to Pro status instantly.
        </DialogDescription>

        <div className="py-6 space-y-4">
          <div className="flex gap-2">
            <Input 
              placeholder="@username" 
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-sonas-surface2 h-11 rounded-xl"
            />
            <Button onClick={findUser} disabled={loading} size="icon" className="h-11 w-11 rounded-xl bg-sonas-surface2 hover:bg-sonas-surface3 border border-sonas-border">
              <Search size={18} />
            </Button>
          </div>

          {targetUser && (
            <div className="p-4 rounded-2xl bg-sonas-accent/5 border border-sonas-accent/20 text-left animate-in fade-in slide-in-from-top-2">
              <p className="text-sm font-bold">{targetUser.full_name}</p>
              <p className="text-[10px] text-sonas-dim font-mono">@{targetUser.username}</p>
              {targetUser.is_pro && <p className="text-[10px] text-sonas-accent font-bold mt-2 uppercase">Already Pro</p>}
            </div>
          )}
        </div>

        <Button 
          className="w-full h-12 bg-sonas-accent text-sonas-bg font-bold rounded-2xl shadow-lg shadow-sonas-accent/20"
          disabled={!targetUser || targetUser.is_pro || loading}
          onClick={handleGift}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Gift size={16} className="mr-2" />}
          Send Pro Access
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default GiftProDialog;